import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import noop from 'lodash/noop';
import { useFetchUserTraits, useUserTraits } from '@atlassian/jira-business-common';
import { isJwmReduceNoiseOnSignupEnabled } from '@atlassian/jira-business-feature-flags';
import { SERVICE_DESK_PROJECT } from '@atlassian/jira-common-constants';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { MiniBody, Mini } from '@atlassian/jira-minimizable-modal';

import { useTriggerIssueCreateModal } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal';
import { isPartOfDiscoverabilityTriggers } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/utils';
import { useAnalyticsEvents, fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useProjectContext } from '@atlassian/jira-providers-project-context';
import { usePersistentIssueCreateModalViewResource } from '@atlassian/jira-router-resources-persistent-issue-create-modal-view';
import Footer, { GIC_VARIANTS } from '../../../../common/ui/footer';
import { MiniHeader } from '../../../../common/ui/minimizable-modal-mini-header';
import { useParseSummaryValue } from '../../../../common/utils/parse-summary-value';

import { useMinimizableModalWrapperProps } from '../../../../controllers/minimizable-modal-wrapper-props';
import { SpotlightContainer } from './modal-spotlight';
import type { MinimizableModalMiniProps } from './types';

const returnNull = () => null;

export const MinimizableModalMini = forwardRef<HTMLElement, MinimizableModalMiniProps>(
	({ children, summaryValue, ...rest }: MinimizableModalMiniProps, ref) => {
		const {
			onModalCloseWithConfirm,
			onSubmit,
			isSubmitting,
			isOperationInProgress,
			canCreateAnotherIssue,
			onCreateAnotherChange,
			getShouldDisableSubmit,
			createdIssues,
			displayCreateAnotherIssueToggle,
			selectedIssueType,
			projectType,
		} = useMinimizableModalWrapperProps();

		const { createAnalyticsEvent } = useAnalyticsEvents();
		const issueTypeRef = useRef(selectedIssueType);
		const { parseSummaryValue } = useParseSummaryValue();
		const [shouldRenderMiniModalSpotlight, setShouldRenderMiniModalSpotlight] =
			useState<boolean>(false);

		const [{ shouldShowMiniModalSpotlight, triggerPointKey }, { dismissModalSpotlight }] =
			useTriggerIssueCreateModal();

		const [userTraits, { setUserTraits }] = isJwmReduceNoiseOnSignupEnabled()
			? // eslint-disable-next-line react-hooks/rules-of-hooks
				useUserTraits()
			: [null, { setUserTraits: noop }];
		const fetchUserTraits = isJwmReduceNoiseOnSignupEnabled()
			? // eslint-disable-next-line react-hooks/rules-of-hooks
				useFetchUserTraits()
			: returnNull;

		const setUserTraitsIfNotAvailable = useCallback(async () => {
			const newTraits = await fetchUserTraits();
			// We must explicitly check for false because fetchUserTraits returns "false" when pending, and "null" when traits are empty.
			if (newTraits !== false) {
				setUserTraits(newTraits);
			}
		}, [fetchUserTraits, setUserTraits]);

		const { updateIsDiscoPushComplete } = usePersistentIssueCreateModalViewResource();

		const { data: projectContext } = useProjectContext();

		const markMiniModalAsSeen = useCallback(() => {
			try {
				const shouldDismiss =
					isPartOfDiscoverabilityTriggers(triggerPointKey) &&
					projectContext?.projectType !== SERVICE_DESK_PROJECT;

				if (shouldDismiss) {
					updateIsDiscoPushComplete(true);
					dismissModalSpotlight();
				}
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					error,
					meta: {
						id: 'setMiniModalSpotlightPreference',
						packageName: 'jiraIssueCreate',
					},
					sendToPrivacyUnsafeSplunk: true,
				});
			}
		}, [
			dismissModalSpotlight,
			triggerPointKey,
			updateIsDiscoPushComplete,
			projectContext?.projectType,
		]);

		useEffect(
			() => () => {
				markMiniModalAsSeen();
			},
			[markMiniModalAsSeen],
		);

		useEffect(() => {
			if (selectedIssueType) {
				issueTypeRef.current = selectedIssueType;
			}
		}, [selectedIssueType]);

		useEffect(() => {
			if (!isJwmReduceNoiseOnSignupEnabled()) {
				setShouldRenderMiniModalSpotlight(!!shouldShowMiniModalSpotlight);
			} else if (!userTraits?.attributes?.length && userTraits?.attributes !== null) {
				setUserTraitsIfNotAvailable();
			} else {
				const accountCreationDate = userTraits?.attributes?.find(
					(trait) => trait?.name === 'jira_first_active',
				)?.value;
				const aWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
				let isNewUser = false;
				if (accountCreationDate && typeof accountCreationDate === 'string') {
					isNewUser = new Date(accountCreationDate) > aWeekAgo;
				}

				setShouldRenderMiniModalSpotlight(!!shouldShowMiniModalSpotlight && !isNewUser);
			}
		}, [userTraits, shouldShowMiniModalSpotlight, setUserTraitsIfNotAvailable]);

		useEffect(() => {
			fireTrackAnalytics(
				createAnalyticsEvent({}),
				shouldRenderMiniModalSpotlight ? 'feature exposed' : 'feature hidden',
				'issueCreateMiniModalSpotlight',
				{
					interruptionType: 'spotlight',
					targetsNewUsers: true,
					jwm_reduce_noise_on_signup_experiment_enabled: isJwmReduceNoiseOnSignupEnabled(),
				},
			);
		}, [createAnalyticsEvent, shouldRenderMiniModalSpotlight]);

		return (
			// @ts-expect-error - TS2746 - This JSX tag's 'children' prop expects a single child of type 'HTMLCollection', but multiple children were provided.
			<Mini ref={ref} {...rest}>
				{shouldRenderMiniModalSpotlight && (
					<SpotlightContainer
						shouldShowSpotlight={shouldRenderMiniModalSpotlight}
						onCloseSpotlight={markMiniModalAsSeen}
					/>
				)}
				<MiniHeader
					title={parseSummaryValue(summaryValue, issueTypeRef.current?.label)}
					isItalic={!summaryValue}
					iconUrl={issueTypeRef.current?.iconUrl}
					showHeaderIcon={projectType !== SERVICE_DESK_PROJECT}
				/>
				<MiniBody>{children}</MiniBody>
				<Footer
					onClose={onModalCloseWithConfirm('cancel')}
					onSubmit={onSubmit}
					isSubmitting={isSubmitting}
					isOperationInProgress={isOperationInProgress}
					canCreateAnotherIssue={canCreateAnotherIssue}
					onCreateAnotherChange={onCreateAnotherChange}
					shouldDisableSubmit={getShouldDisableSubmit()}
					createdIssues={createdIssues}
					variant={GIC_VARIANTS.MINI}
					displayCreateAnotherIssueToggle={displayCreateAnotherIssueToggle}
				/>
			</Mini>
		);
	},
);
